'use client';

import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';

/**
 * Slide-and-fade reveal for a hero stat block.
 *
 * Deliberately free of ScrollTrigger: the reveal point is read from live
 * geometry (a synchronous measurement at mount, then IntersectionObserver), so
 * it cannot be left stranded when a pinned section, a late image load or the
 * Lenis proxy shifts the element after a trigger position was cached.
 */

const DEFAULT_START = '0 70%';

/**
 * Reads the viewport fraction out of a GSAP-style start string such as
 * "0 70%" — the point in the viewport where the reveal should fire.
 */
const parseStartFraction = start => {
  const viewportEdge = String(start || DEFAULT_START)
    .trim()
    .split(/\s+/)[1];

  if (viewportEdge === 'top') return 0;
  if (viewportEdge === 'center') return 0.5;
  if (viewportEdge === 'bottom') return 1;

  const percentage = parseFloat(viewportEdge);
  return Number.isFinite(percentage) ? percentage / 100 : 0.7;
};

export default function HeroStatsRevealStandalone({
  children,
  y = 50,
  duration = 1.2,
  delay = 0.8,
  start = DEFAULT_START,
}) {
  const statsRef = useRef(null);

  useLayoutEffect(() => {
    const element = statsRef.current;
    if (!element) return undefined;

    const fraction = parseStartFraction(start);

    let tween = null;
    const reveal = () => {
      tween?.kill();
      tween = gsap.to(element, {
        yPercent: 0,
        opacity: 1,
        duration,
        ease: 'power4.out',
        delay,
        overwrite: 'auto',
      });
    };

    gsap.set(element, { yPercent: y, opacity: 0 });

    if (typeof IntersectionObserver === 'undefined') {
      reveal();
      return () => tween?.kill();
    }

    // No measurement is taken at mount on purpose. The hero above this row has
    // not loaded yet at that point, so the row measures as being at the top of
    // the page and would reveal itself while it is still far below the fold.
    // The observer re-evaluates as the layout settles, so it can only fire once
    // the row is genuinely at the start line.
    const bottomInset = Math.round((1 - fraction) * 100);
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          reveal();
          observer.disconnect();
        }
      },
      { rootMargin: `0px 0px -${bottomInset}% 0px`, threshold: 0 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      tween?.kill();
    };
  }, [y, duration, delay, start]);

  return <div ref={statsRef}>{children}</div>;
}
